import ArrowDownward from '@mui/icons-material/ArrowDownward'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import GetAppIcon from '@mui/icons-material/GetApp'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import {
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  ListItemIcon,
  styled,
} from '@mui/material'
import { fetchNormalDocuments } from 'api/documents'
import OverflowText from 'components/ui/OverflowText'
import { checkVerifyCsvDownloadable } from 'entities/BaseDocuments'
import { DocListItem } from 'entities/Documents'
import { useAlert } from 'hooks/useDialog'
import useMode from 'hooks/useMode'
import { useEffect, useState, useCallback, useRef } from 'react'
import { timeWithoutYear } from 'utils/format'
import { classNames } from 'utils/reactHelpers'

import useGoToFile from './useGoToFile'

const Loading = styled(CircularProgress)({
  position: 'absolute',
  left: '50%',
  top: '50%',
  marginLeft: '-20px',
  marginTop: '-20px',
})

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  width: theme.spacing(25),
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
}))

const BTN_H = 64 // height of <AppBar>
const SortBtn = styled('div')(({ theme }) => ({
  height: BTN_H,
  position: 'relative',
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.grey[300],
  cursor: 'pointer',
  userSelect: 'none',
  '&:not(.downloading):hover': {
    backgroundColor: theme.palette.grey[400],
  },
}))

const BatchCsvDownloadBtn = styled('div')(({ theme }) => ({
  height: BTN_H,
  position: 'relative',
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: 'auto',
  backgroundColor: theme.palette.grey[300],
  cursor: 'pointer',
  userSelect: 'none',
  '&:not(.downloading):hover': {
    backgroundColor: theme.palette.grey[400],
  },
}))

type SORT = 0 | 1 | -1

type Props = {
  objIDs: string[]
  sortedIDs: string[] | null
  objectID?: string
  currentIdx: number
  onDownload: (fromMulti: boolean) => void
  downloading: boolean
  changed: number
  setSortedIDs: (ids: string[]) => void
  setOIdx: React.Dispatch<React.SetStateAction<number>>
}

const LOT = 80

export default function MultiDocList({
  objIDs,
  sortedIDs: _sortedIDs,
  objectID,
  currentIdx,
  onDownload,
  downloading,
  changed,
  setSortedIDs,
  setOIdx,
}: Props) {
  const alert = useAlert()
  const goToFile = useGoToFile(setOIdx)
  const [reloadCnt, setReloadCnt] = useState(0)
  const { mode } = useMode()
  const [sort, setSort] = useState<SORT>(0)
  const [documents, setDocuments] = useState<DocListItem[] | null>(null)

  const cRef = useRef<HTMLDivElement>(null)

  const setNewOIdx = useCallback(
    (documents: DocListItem[], objectID: string, cIdx: number) => {
      const newOidx = documents.findIndex((d) => d.objectID === objectID)
      if (newOidx === -1) {
        // deleted?
        // keeps oIdx
        // 削除されて場合 oidxがlistより大きい場合documents.lengthとresetされる
        // 新規読み込んだ文書のlengthが現在のidxよりlessの場合
        if (cIdx > documents.length - 1) setOIdx(documents.length - 1)
        // ここで currentIdxを変更すると reloadがまた実装される
      } else {
        // sorted
        setOIdx(newOidx)
      }
    },
    [setOIdx],
  )

  const reloadDocs = useCallback(
    async (objIDs: string[], sort: SORT, objID: string | undefined, cIdx: number) => {
      const idsChunks: Props['objIDs'][] = []
      for (let i = 0; i < objIDs.length; i += LOT) {
        idsChunks.push(objIDs.slice(i, i + LOT))
      }
      const fetched = await Promise.all(
        idsChunks.map((ids) => {
          return fetchNormalDocuments({
            // BE CAREFUL: verify doc ids and normal doc ids can't be mixed!
            id: ids,
          })
        }),
      )
      const docInfoList = fetched.map((f) => f.docInfoList || []).flat()
      // objIDsとdocInfoListは一致しない可能性もある
      if (docInfoList.length === 0) {
        alert({ message: '読み込みに失敗しました。' })
        setDocuments([])
        setSortedIDs([])
      } else {
        /* sorted on the server side
        docInfoList.sort(
          (a, b) => objIDs.indexOf(a.objectID) - objIDs.indexOf(b.objectID))
        */
        const sortedDocuments = docInfoList.sort(
          (a, b) => sort * a.documentName.localeCompare(b.documentName),
        )
        setDocuments(sortedDocuments)
        setSortedIDs(sortedDocuments.map((d) => d.objectID))
        objID && setNewOIdx(sortedDocuments, objID, cIdx)
      }
    },
    [alert, setSortedIDs, setNewOIdx],
  )

  useEffect(() => {
    const _dummy = changed
    reloadDocs(objIDs, sort, objectID, currentIdx).then(() => {
      setReloadCnt((r) => r + 1)
    })
    // TODO: ignore objectID and currentIdx only
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [reloadDocs, objIDs, changed, sort])

  useEffect(() => {
    cRef.current?.scrollIntoView()
  }, [reloadCnt])

  const allCsvDownloadable =
    mode === 'normal' || documents?.every((doc) => checkVerifyCsvDownloadable(doc.documentStatusNo))

  const items = documents?.map(({ objectID, documentName, updateDateTime }, idx) => {
    const selected = idx === currentIdx
    return (
      <ListItemButton
        key={objectID}
        selected={selected}
        ref={selected ? cRef : undefined}
        onClick={() => {
          !selected && goToFile(idx)
        }}
        data-testid={`edit-documents-list${idx}`}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          {selected ? (
            <InsertDriveFileIcon fontSize="small" />
          ) : (
            <InsertDriveFileOutlinedIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={<OverflowText line={3}>{documentName}</OverflowText>}
          primaryTypographyProps={{ variant: 'subtitle2' }}
          secondary={`${updateDateTime ? timeWithoutYear(updateDateTime) : ''}`}
        />
      </ListItemButton>
    )
  })

  const toggleSort = useCallback(
    (sort: SORT) => {
      setSort(sort === 0 ? 1 : (-sort as 1 | -1))
    },
    [setSort],
  )

  return (
    <Container>
      <SortBtn onClick={() => toggleSort(sort)} data-testid="sort-button">
        名前で並べ替え
        {sort === -1 && <ArrowDownward />}
        {sort === 1 && <ArrowUpward />}
      </SortBtn>
      <List
        component="nav"
        style={{
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        {items}
      </List>

      {allCsvDownloadable && (
        <BatchCsvDownloadBtn
          className={classNames({ downloading })}
          onClick={() => onDownload(true)}
          data-testid="download-results-button"
        >
          {downloading && <Loading />}
          <GetAppIcon />
          &nbsp;一括結果ダウンロード
        </BatchCsvDownloadBtn>
      )}
    </Container>
  )
}
